import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { CatalogUerBaseCostWorkScopeData, CollectionUerBaseCostWorkScopeData } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { Button, cnMixSpace } from 'uikit';

import type { AgGridEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type React from 'react';
import { useCallback, useRef } from 'react';

import { StateName, useEditState } from './hooks/useEditState';
import type { TUseUpdatePriceWorkScope } from './hooks/useUpdateWorkScope';
import { useUpdateWorkScope } from './hooks/useUpdateWorkScope';
import { useUpdateWorkScopeColumns } from './hooks/useUpdateWorkScopeColumns';
import { useUpdatePriceWorkScopeData } from './hooks/useUpdateWorkScopeDataLocalState';
import type { IRowWithCommonOptions } from './types';

import Styles from './BaseCostWorkScopesEditModal.module.css';

export type TUseUpdateWorkScopeData = (props?: Partial<{}>) => {
    items: CollectionUerBaseCostWorkScopeData[] | CatalogUerBaseCostWorkScopeData[];
    status: RequestStatus;
};

interface IBaseCostWorkScopesEditModal {
    onClose: () => void;
    onRefresh: () => void;
    useUpdateWorkScopeData: TUseUpdateWorkScopeData;
    useUpdateWorkScopeDataParams?: Partial<{}>;
    useUpdate: TUseUpdatePriceWorkScope;
    useUpdateParams?: Partial<{}>;
}

export const BaseCostWorkScopesEditModal: React.FC<IBaseCostWorkScopesEditModal> = (props) => {
    const { onClose, onRefresh, useUpdateWorkScopeData, useUpdateWorkScopeDataParams, useUpdate, useUpdateParams } =
        props;

    const gridRef = useRef<AgGridReact<IRowWithCommonOptions>>(null);

    const { isDirty, isFormFrozen, isEditInProgress, setValueState } = useEditState();

    const columns = useUpdateWorkScopeColumns({ setValueState, isKebabDisabled: isEditInProgress });
    const { items, status } = useUpdateWorkScopeData(useUpdateWorkScopeDataParams);
    const localRowState = useUpdatePriceWorkScopeData({ items });

    const createNewItem = useCallback(() => {
        gridRef?.current?.api?.ensureNodeVisible((r) => r.lastChild);

        gridRef?.current?.api?.applyTransaction({
            add: [{ isEditing: false, isCreating: true, name: '', id: `${new Date()}`, number: undefined }],
            addIndex: undefined,
        });

        setValueState(StateName.isFrozen, false);
        setValueState(StateName.isEditInProgress, true);
    }, [setValueState]);

    const { handleUpdate, isUpdating } = useUpdateWorkScope({
        api: gridRef?.current?.api,
        onClose,
        onRefresh,
        useUpdate,
        useUpdateParams,
    });

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const closeHandler = useCallback(() => {
        if (isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [handleOpenConfirmationDialog, isDirty, onClose]);

    return (
        <ModalDialog
            isOpen
            withTitleCloseButton
            title="Редактирование состава работ"
            onClose={closeHandler}
            isCommit={isUpdating}
            isOnClickOutsideDisable
            onConfirm={handleUpdate}
            btnConfirmDisabled={isFormFrozen || isEditInProgress}
            isLoading={status === RequestStatus.FETCHING}
        >
            <div className={cnMixSpace({ p: 'm' })}>
                <Button
                    label="Создать работу"
                    size="s"
                    view="secondary"
                    onClick={createNewItem}
                    disabled={isEditInProgress}
                />

                <div className={Styles.updateTable}>
                    <AgGridTable
                        ref={gridRef}
                        suppressCellFocus
                        headerHeight={0}
                        getRowId={({ data }) => `${data?.id}`}
                        rowData={localRowState}
                        columnDefs={columns}
                        onRowDragEnd={(e: AgGridEvent) => {
                            e.api.refreshCells();
                        }}
                        onDragStopped={() => {
                            setValueState(StateName.isFrozen, false);
                        }}
                        rowDragManaged
                    />
                </div>
            </div>
            <ConfirmationDialog
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
            />
        </ModalDialog>
    );
};

BaseCostWorkScopesEditModal.displayName = 'BaseCostWorkScopesEditModal';
